import { ILaya } from "../../ILaya";
import { Laya } from "../../Laya";
import { Event } from "../events/Event";
import { Vector4 } from "../maths/Vector4";
import { Loader } from "../net/Loader";
import { RenderTargetFormat } from "../RenderEngine/RenderEnum/RenderTargetFormat";
import { Texture } from "../resource/Texture";
import { LargeTexManager } from "./LargeTexManager";
export class TextureInfo {
    recover() {
        this.owner.removeByTexture2DId(this.textureId, -1, false);
    }
}
export class DynamicAtlasManager {
    constructor(config, autoReplace = true) {
        this._textureMap = new Map();
        this._textureIdMap = new Map();
        this._isDestroyed = false;
        this._autoReplace = false;
        this._totalDrawCount = 1;
        this._waitReplace = new Set();
        this._config = Object.assign({ largeTextureSize: [1024, 1024], maxLargeTextures: 4, textureUnitSize: 16, extendSize: 1, textureFormat: RenderTargetFormat.R8G8B8A8, immediately: false, autoExtend: true, checkDuplicate: true }, config);
        this._largeTexManager = new LargeTexManager(this._config.largeTextureSize, this._config.maxLargeTextures, this._config.textureUnitSize, this._config.extendSize, this._config.textureFormat);
        this._largeTexManager.gammaCorrection = 2.2;
        this._largeTexManager.sRGB = false;
        this._largeTexManager.immediately = this._config.immediately;
        this._largeTexManager.autoExtend = this._config.autoExtend;
        this._largeTexManager.checkDup = this._config.checkDuplicate;
        this._largeTexManager.name = "DynamicAtlas";
        this._autoReplace = autoReplace;
        if (autoReplace) {
            this._largeTexManager.updateHook = this.afterUpdate.bind(this);
        }
        this._totalDrawCount = this._config.extendSize > 0 ? 2 : 1;
    }
    _getTexture2DByUrl(url) {
        if (!url)
            return null;
        let texture2D = Laya.loader.getRes(url, Loader.TEXTURE2D);
        if (texture2D)
            return texture2D;
        let texture = Laya.loader.getRes(url, Loader.IMAGE);
        if (texture && texture.bitmap) {
            return texture.bitmap;
        }
        return null;
    }
    _getTexture2DId(textureId) {
        return this._textureIdMap.get(textureId) || null;
    }
    _findSmallTexture(tex, info) {
        let out = info.textureMap;
        let ot = tex.bitmap;
        let texture2DId = info.textureId;
        const _find = (texture) => {
            let atlas = texture._atlas;
            if (atlas) {
                atlas.frames.forEach(_t => {
                    if (_t.bitmap == ot) {
                        _t._dynamic = info;
                        out.set(_t.id, { texture: _t, uv: _t.uv });
                        this._textureIdMap.set(_t.id, texture2DId);
                    }
                });
                atlas.textures.forEach(_t => {
                    if (_t.bitmap == ot) {
                        _t._dynamic = info;
                        out.set(_t.id, { texture: _t, uv: _t.uv });
                        this._textureIdMap.set(_t.id, texture2DId);
                    }
                });
            }
        };
        _find(tex);
        if (tex._dynamic != info) {
            out.set(tex.id, { texture: tex, uv: tex.uv });
            tex._dynamic = info;
            this._textureIdMap.set(tex.id, texture2DId);
        }
    }
    addTexture(texture, scale = 1.0, largeTextureIndex = -1) {
        if (this._isDestroyed
            || !texture
            || !texture.bitmap) {
            return false;
        }
        let texture2D = texture.bitmap;
        let textureId = texture2D.id;
        let textureInfo = this._textureMap.get(textureId);
        if (textureInfo) {
            let textureMap = textureInfo.textureMap;
            if (textureMap.has(texture.id)) {
                return true;
            }
            textureMap.set(texture.id, {
                texture, uv: texture.uv
            });
            this._textureIdMap.set(texture.id, textureId);
            if (this._autoReplace && textureInfo.merged) {
                this._waitReplace.add(textureId);
                Laya.timer.callLater(this, this.onUpdate);
            }
            return true;
        }
        let result = this._largeTexManager.addTexture(texture2D, scale, largeTextureIndex, texture.url, false);
        if (result < 0) {
            console.warn(`DynamicAtlasManager: 纹理 ${textureId} 添加失败，空间不足`);
            return false;
        }
        let textureOut = this._largeTexManager.getTexture(textureId, result);
        textureInfo = new TextureInfo();
        textureInfo.textureMap = new Map();
        textureInfo.source = texture2D;
        textureInfo.textureId = textureId;
        textureInfo.url = texture2D.url || "";
        textureInfo.uv = new Vector4(textureOut.texItem.x, textureOut.texItem.y, textureOut.texItem.w, textureOut.texItem.h);
        textureInfo.largeTextureIndex = result;
        textureInfo.isInAtlas = true;
        textureInfo.merged = false;
        textureInfo.drawCompletedCount = 0;
        textureInfo.referenceCount = 0;
        textureInfo.owner = this;
        this._findSmallTexture(texture, textureInfo);
        this._textureMap.set(textureId, textureInfo);
        return true;
    }
    onUpdate() {
        if (this._waitReplace.size > 0) {
            for (const textureId of this._waitReplace) {
                let textureInfo = this._textureMap.get(textureId);
                if (textureInfo) {
                    this._replaceTexture(textureInfo, this._largeTexManager.getTexture(textureId, textureInfo.largeTextureIndex));
                }
            }
        }
        this._waitReplace.clear();
    }
    afterUpdate(index, completedIds) {
        if (this._isDestroyed || !completedIds || completedIds.length === 0)
            return;
        for (const id of completedIds) {
            let textureInfo = this._textureMap.get(id);
            if (!textureInfo || !textureInfo.isInAtlas) {
                continue;
            }
            if (textureInfo.merged) {
                continue;
            }
            textureInfo.drawCompletedCount++;
            if (textureInfo.drawCompletedCount < this._totalDrawCount) {
                continue;
            }
            const textureOut = this._largeTexManager.getTexture(id, index);
            if (!textureOut || !textureOut.texture || !textureOut.texItem) {
                continue;
            }
            this._replaceTexture(textureInfo, textureOut);
        }
    }
    addTextureByUrl(url, scale = 1.0, largeTextureIndex = -1) {
        if (this._isDestroyed || !url) {
            return false;
        }
        let texture = ILaya.loader.getRes(url, Loader.IMAGE);
        if (!texture) {
            console.warn(`DynamicAtlasManager: 纹理 ${url} 未加载，请先加载纹理`);
            return false;
        }
        if (!texture.bitmap) {
            console.warn(`DynamicAtlasManager: 纹理 ${url} 的 bitmap 为空，无法添加到图集`);
            return false;
        }
        return this.addTexture(texture, scale, largeTextureIndex);
    }
    addTextures(textures, scale = 1.0, largeTextureIndex = -1) {
        let successCount = 0;
        for (const texture of textures) {
            if (this.addTexture(texture, scale, largeTextureIndex)) {
                successCount++;
            }
        }
        return successCount;
    }
    addTexturesByUrl(urls, scale = 1.0, largeTextureIndex = -1) {
        let successCount = 0;
        for (const url of urls) {
            if (this.addTextureByUrl(url, scale, largeTextureIndex)) {
                successCount++;
            }
        }
        return successCount;
    }
    removeTexture(texture, largeTextureIndex = -1, event = true) {
        if (this._isDestroyed)
            return false;
        return this.removeByTexture2DId(this._getTexture2DId(typeof texture === 'number' ? texture : texture.id), largeTextureIndex);
    }
    removeByTexture2DId(texture2DId, largeTextureIndex = -1, event = true) {
        if (this._isDestroyed)
            return false;
        let textureInfo = this._textureMap.get(texture2DId);
        if (!textureInfo)
            return false;
        this._largeTexManager.removeTexture(texture2DId, largeTextureIndex);
        this._textureMap.delete(texture2DId);
        let otexture2d = textureInfo.source;
        if (!otexture2d || otexture2d.destroyed) {
            if (textureInfo.url) {
                otexture2d = Laya.loader.getRes(textureInfo.url, Loader.TEXTURE2D);
            }
        }
        if (!otexture2d || otexture2d.destroyed) {
            return true;
        }
        textureInfo.textureMap.forEach(({ texture, uv }, id) => {
            texture.bitmap = otexture2d;
            texture.uv = uv;
            texture._dynamic = null;
            this._textureIdMap.delete(id);
            if (event) {
                texture.event(Event.CHANGE);
            }
        });
        return true;
    }
    removeTextureByUrl(url, largeTextureIndex = -1) {
        if (!url)
            return false;
        let texture = Laya.loader.getRes(url, Loader.IMAGE);
        if (texture && texture._dynamic) {
            let textureInfo = texture._dynamic;
            return this.removeByTexture2DId(textureInfo.textureId, largeTextureIndex);
        }
        for (let [textureId, textureInfo] of this._textureMap) {
            if (textureInfo.url === url) {
                return this.removeByTexture2DId(textureId, largeTextureIndex);
            }
        }
        console.warn(`DynamicAtlasManager: 纹理 ${url} 未在图集中找到`);
        return false;
    }
    _replaceTexture(textureInfo, textureOut) {
        textureInfo.merged = true;
        let textureMap = textureInfo.textureMap;
        let rt = textureOut.texture;
        let x = textureOut.texItem.x;
        let y = textureOut.texItem.y;
        let w = textureOut.texItem.w;
        let h = textureOut.texItem.h;
        textureMap.forEach(({ texture, uv }, id) => {
            if (texture.bitmap !== rt) {
                let oSWidth = texture.sourceWidth;
                let oSHeight = texture.sourceHeight;
                let oWidth = texture.width;
                let oHeight = texture.height;
                let nuv;
                if (uv === Texture.DEF_UV) {
                    nuv = Float32Array.from([
                        x, y,
                        x + w, y,
                        x + w, y + h,
                        x, y + h
                    ]);
                }
                else {
                    let ox = uv[0];
                    let oy = uv[1];
                    let owidth = uv[2] - ox;
                    let oheight = uv[5] - oy;
                    let nx = x + ox * w;
                    let ny = y + oy * h;
                    let nwidth = owidth * w;
                    let nheight = oheight * h;
                    nuv = Float32Array.from([
                        nx, ny,
                        nx + nwidth, ny,
                        nx + nwidth, ny + nheight,
                        nx, ny + nheight
                    ]);
                }
                texture.setTo(rt, nuv, oSWidth, oSHeight);
                texture.event(Event.CHANGE);
                texture.width = oWidth;
                texture.height = oHeight;
                texture._dynamic = textureInfo;
            }
        });
        return true;
    }
    replaceOriginalTexture(textureId) {
        if (this._isDestroyed)
            return false;
        let textureInfo = this._textureMap.get(textureId);
        if (!textureInfo || !textureInfo.isInAtlas) {
            return false;
        }
        const textureOut = this._largeTexManager.getTexture(textureId, textureInfo.largeTextureIndex);
        if (!textureOut || !textureOut.texture || !textureOut.texItem) {
            console.warn(`DynamicAtlasManager: 无法获取纹理 ${textureId} 的图集信息`);
            return false;
        }
        this._replaceTexture(textureInfo, textureOut);
        return true;
    }
    replaceOriginalTextures(textureIds) {
        let successCount = 0;
        if (textureIds && textureIds.length > 0) {
            for (const textureId of textureIds) {
                if (this.replaceOriginalTexture(textureId)) {
                    successCount++;
                }
            }
        }
        else {
            for (const [textureId, textureInfo] of this._textureMap) {
                if (textureInfo.isInAtlas && this.replaceOriginalTexture(textureId)) {
                    successCount++;
                }
            }
        }
        return successCount;
    }
    getTextureInfo(textureId) {
        return this._textureMap.get(textureId) || null;
    }
    getTextureInfoByUrl(url) {
        if (!url)
            return null;
        let texture2D = this._getTexture2DByUrl(url);
        if (!texture2D)
            return null;
        return this.getTextureInfo(texture2D.id) || null;
    }
    getLargeTexture(largeTextureIndex) {
        return this._largeTexManager.getLargeTex(largeTextureIndex);
    }
    getAllLargeTextures() {
        return this._largeTexManager.largeTexs;
    }
    getTextureCount() {
        return this._textureMap.size;
    }
    clear() {
        this._largeTexManager.clear();
        this._textureMap.clear();
        this._textureIdMap.clear();
    }
    destroy() {
        if (this._isDestroyed) {
            return;
        }
        this.clear();
        this._isDestroyed = true;
    }
    getStatistics() {
        return {
            textureCount: this.getTextureCount(),
            usageRate: this._largeTexManager.getLoadUsageRate(),
            gpuMemoryUsage: this._largeTexManager.calculateGpuMemory(),
            largeTextureCount: this._largeTexManager.largeTexs.length,
            config: this._config
        };
    }
    cleanupUnusedTextures(forceClean = false) {
        if (this._isDestroyed)
            return 0;
        let cleanedCount = 0;
        const textureIdsToRemove = [];
        for (const [textureId, textureInfo] of this._textureMap) {
            if (forceClean || textureInfo.referenceCount <= 0) {
                textureIdsToRemove.push(textureId);
            }
        }
        for (const textureId of textureIdsToRemove) {
            if (this.removeByTexture2DId(textureId)) {
                cleanedCount++;
            }
        }
        return cleanedCount;
    }
}
